import { useCallback, useMemo } from "react";
import { Button } from "@legal-500/legal500-design-system";
import type { ApplyRowResult, ScanRow } from "../../../../api/roleProvisioning.service.js";
import { downloadAppliedCsv, downloadSkippedCsv } from "./downloadCsv.js";
import type { CsvRecord, ParsedCsv } from "./parseCsv.js";
import type { PreviewRow } from "./previewRows.js";
import { ResultsTable, countByStatus } from "./ResultsTable.js";
import styles from "./RoleProvisioning.module.css";

/**
 * The "done" phase (migration-plan.md §6.5): summary counts, the per-row
 * results, and the two downloads — skipped.csv for CS follow-up and
 * applied.csv as the undo list.
 */

type ResultsStepProps = {
  applied: ApplyRowResult[];
  scanRows: ScanRow[];
  previewRows: PreviewRow[];
  parsed: ParsedCsv | null;
  onScanAgain: () => void;
};

export function ResultsStep({
  applied,
  scanRows,
  previewRows,
  parsed,
  onScanAgain,
}: ResultsStepProps) {
  const emailByLine = useMemo(
    () => new Map(scanRows.map(row => [row.lineNumber, row.email] as const)),
    [scanRows],
  );
  const emailFor = useCallback(
    (lineNumber: number) => emailByLine.get(lineNumber) ?? null,
    [emailByLine],
  );

  const recordsByLine = useMemo(
    () =>
      new Map<number, CsvRecord>(
        (parsed?.records ?? []).map(record => [record.lineNumber, record] as const),
      ),
    [parsed],
  );
  const skippedRows = useMemo(
    () => previewRows.filter(row => row.group === "skipped"),
    [previewRows],
  );

  const counts = useMemo(() => countByStatus(applied), [applied]);
  const writtenCount = counts("inserted") + counts("created");
  const undoableCount = applied.filter(result => (result.rolesInserted ?? []).length > 0).length;

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Result</div>
        <span className={styles.counts}>
          {`Inserted ${counts("inserted")} · Created ${counts("created")} · ` +
            `Already had roles ${counts("already_has_roles")} · ` +
            `Not a member ${counts("not_a_member")} · ` +
            `Already exists ${counts("already_exists")} · Error ${counts("error")}`}
        </span>
      </div>

      <ResultsTable results={applied} emailFor={emailFor} />

      <div className={styles.toolbar}>
        <Button variant="outlined" onClick={onScanAgain}>
          Scan again
        </Button>
        <span className={styles.rowNote}>
          {writtenCount === 0
            ? "Nothing was written."
            : "applied.csv is the undo list (userId, organisationId, role, created)."}
        </span>
        <span className={styles.spacer} />
        <Button
          variant="outlined"
          disabled={skippedRows.length === 0}
          onClick={() =>
            downloadSkippedCsv(parsed?.headers ?? [], recordsByLine, skippedRows, new Date())
          }
        >
          Download skipped.csv
        </Button>
        <Button
          variant="contained"
          disabled={undoableCount === 0}
          onClick={() => downloadAppliedCsv(applied, new Date())}
        >
          Download applied.csv
        </Button>
      </div>
    </>
  );
}
